import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  V2Badge,
  V2Button,
  V2Card,
  V2CardHeader,
  V2DataGrid,
  V2EmptyState,
  V2FormField,
  V2FormGrid,
  LoadingState,
  StatePanel,
  V2CloseButton,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { formatRialFa } from '../../lib/finance-display.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import type { FiscalPeriodListResponse, FiscalYearListResponse } from '../../lib/types/finance.js';

interface CostTarget {
  readonly targetId: string;
  readonly ruleId: string;
  readonly targetAccountId: string;
  readonly targetDimensionValueId: string | null;
  readonly weightRatio: string;
}

interface CostRule {
  readonly ruleId: string;
  readonly code: string;
  readonly name: string;
  readonly version: number;
  readonly sourceAccountId: string;
  readonly sourceDimensionValueId: string | null;
  readonly creditAccountId: string;
  readonly basisDescription: string;
  readonly status: string;
  readonly targets: CostTarget[];
  readonly createdAt: string;
}

interface CostRun {
  readonly runId: string;
  readonly ruleId: string;
  readonly fiscalYearId: string;
  readonly fiscalPeriodId: string;
  readonly runDate: string;
  readonly poolAmountRial: string;
  readonly allocatedAmountRial: string;
  readonly residualAmountRial: string;
  readonly journalId: string | null;
  readonly status: string;
}

interface SimulationResult {
  readonly ruleId: string;
  readonly ruleCode: string;
  readonly poolAmountRial: string;
  readonly totalAllocatedRial: string;
  readonly residualRial: string;
  readonly lines: Array<{
    readonly targetAccountId: string;
    readonly targetDimensionValueId: string | null;
    readonly weightRatio: string;
    readonly allocatedRial: string;
  }>;
}

interface AccountListItem {
  readonly accountId: string;
  readonly code: string;
  readonly name: string;
}

export function CostAllocationsPage() {
  const { unit, label } = useAmountDisplay();
  const { slug } = useParams<{ slug: string }>();
  const base = tenantBasePath(slug ?? '');

  const [activeTab, setActiveTab] = useState<'rules' | 'simulate' | 'runs'>('rules');
  const [refreshKey, setRefreshKey] = useState(0);

  // Modals
  const [showCreateRuleModal, setShowCreateRuleModal] = useState(false);
  const [showExecuteModal, setShowExecuteModal] = useState(false);
  const [selectedRuleId, setSelectedRuleId] = useState('');

  // Create rule form
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [sourceAccountId, setSourceAccountId] = useState('');
  const [creditAccountId, setCreditAccountId] = useState('');
  const [basisDescription, setBasisDescription] = useState('بر اساس نفر-ساعت / سهم متراژ');
  const [targetRows, setTargetRows] = useState<
    Array<{ targetAccountId: string; weightRatio: string }>
  >([
    { targetAccountId: '', weightRatio: '50' },
    { targetAccountId: '', weightRatio: '50' },
  ]);

  // Execute form
  const [execFiscalYearId, setExecFiscalYearId] = useState('');
  const [execFiscalPeriodId, setExecFiscalPeriodId] = useState('');
  const [execRunDate, setExecRunDate] = useState('');

  // Simulation form & state
  const [simRuleId, setSimRuleId] = useState('');
  const [simFiscalYearId, setSimFiscalYearId] = useState('');
  const [simFiscalPeriodId, setSimFiscalPeriodId] = useState('');
  const [simAsOfDate, setSimAsOfDate] = useState('');
  const [simulationResult, setSimulationResult] = useState<SimulationResult | null>(null);

  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const rules = useApiGet<{ items: CostRule[] }>(
    slug ? `${base}/api/v1/finance/cost-allocation/rules` : null,
    [slug, refreshKey],
  );

  const runs = useApiGet<{ items: CostRun[] }>(
    slug ? `${base}/api/v1/finance/cost-allocation/runs` : null,
    [slug, refreshKey],
  );

  const accounts = useApiGet<{ items: AccountListItem[] }>(
    slug ? `${base}/api/v1/finance/accounts` : null,
    [slug],
  );

  const years = useApiGet<FiscalYearListResponse>(
    slug ? `${base}/api/v1/finance/fiscal-years` : null,
    [slug],
  );

  const periods = useApiGet<FiscalPeriodListResponse>(
    slug ? `${base}/api/v1/accounting/fiscal-periods` : null,
    [slug],
  );

  if (!slug) return null;

  async function handleCreateRule(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setActionError(null);
    try {
      await apiPost(`${base}/api/v1/finance/cost-allocation/rules`, {
        code,
        name,
        sourceAccountId,
        creditAccountId,
        basisDescription,
        targets: targetRows.filter((r) => r.targetAccountId && Number(r.weightRatio) > 0),
      });
      setShowCreateRuleModal(false);
      setRefreshKey((k) => k + 1);
      setCode('');
      setName('');
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'خطا در تعریف قاعده تسهیم');
    } finally {
      setBusy(false);
    }
  }

  async function handleRunSimulation(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setActionError(null);
    setSimulationResult(null);
    try {
      const res = await apiPost<SimulationResult>(
        `${base}/api/v1/finance/cost-allocation/simulate`,
        {
          ruleId: simRuleId,
          fiscalYearId: simFiscalYearId,
          fiscalPeriodId: simFiscalPeriodId,
          asOfDate: simAsOfDate,
        },
      );
      setSimulationResult(res);
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'خطا در محاسبه شبیه‌سازی تسهیم');
    } finally {
      setBusy(false);
    }
  }

  async function handleExecuteAllocation(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setActionError(null);
    try {
      await apiPost(`${base}/api/v1/finance/cost-allocation/execute`, {
        ruleId: selectedRuleId,
        fiscalYearId: execFiscalYearId,
        fiscalPeriodId: execFiscalPeriodId,
        runDate: execRunDate,
      });
      setShowExecuteModal(false);
      setRefreshKey((k) => k + 1);
      setActiveTab('runs');
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'خطا در اجرای تسهیم بهای تمام‌شده');
    } finally {
      setBusy(false);
    }
  }

  function addTargetRow() {
    setTargetRows([...targetRows, { targetAccountId: '', weightRatio: '10' }]);
  }

  function removeTargetRow(idx: number) {
    if (targetRows.length <= 1) return;
    setTargetRows(targetRows.filter((_, i) => i !== idx));
  }

  return (
    <div className="sk-page-container">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
        }}
      >
        <div>
          <h1 style={{ fontSize: '1.4rem', fontWeight: 700, margin: 0 }}>
            بهای تمام‌شده و تسهیم هزینه‌ها
          </h1>
          <p
            style={{
              color: 'var(--sk-text-muted, #666)',
              margin: '4px 0 0 0',
              fontSize: '0.875rem',
            }}
          >
            مدیریت قواعد تسهیم هزینه‌های مشترک، شبیه‌سازی بدون تغییر، و صدور پیش‌نویس اسناد تسهیم
            (FIN-ADV-07)
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {activeTab === 'rules' && (
            <V2Button variant="primary" onClick={() => setShowCreateRuleModal(true)}>
              + تعریف قاعده تسهیم جدید
            </V2Button>
          )}
        </div>
      </div>

      {actionError && (
        <div
          style={{
            padding: '12px 16px',
            backgroundColor: 'var(--sk-danger-soft, #fee2e2)',
            borderRadius: '8px',
            color: 'var(--sk-danger, #ef4444)',
            fontSize: '13px',
            marginBottom: 16,
          }}
        >
          {actionError}
        </div>
      )}

      {/* Tabs */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          borderBottom: '1px solid var(--sk-border-color, #e0e0e0)',
          marginBottom: 20,
        }}
      >
        <button
          type="button"
          onClick={() => setActiveTab('rules')}
          style={{
            padding: '8px 16px',
            border: 'none',
            background: 'none',
            borderBottom:
              activeTab === 'rules' ? '2px solid var(--sk-color-primary, #0284c7)' : 'none',
            fontWeight: activeTab === 'rules' ? 700 : 400,
            cursor: 'pointer',
          }}
        >
          قواعد تسهیم (Rules)
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('simulate')}
          style={{
            padding: '8px 16px',
            border: 'none',
            background: 'none',
            borderBottom:
              activeTab === 'simulate' ? '2px solid var(--sk-color-primary, #0284c7)' : 'none',
            fontWeight: activeTab === 'simulate' ? 700 : 400,
            cursor: 'pointer',
          }}
        >
          شبیه‌سازی تسهیم (بدون تغییر)
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('runs')}
          style={{
            padding: '8px 16px',
            border: 'none',
            background: 'none',
            borderBottom:
              activeTab === 'runs' ? '2px solid var(--sk-color-primary, #0284c7)' : 'none',
            fontWeight: activeTab === 'runs' ? 700 : 400,
            cursor: 'pointer',
          }}
        >
          سوابق اجراهای تسهیم
        </button>
      </div>

      {/* Tab 1: Rules */}
      {activeTab === 'rules' && (
        <V2Card>
          <V2CardHeader
            title="قواعد تسهیم تعریف‌شده"
            subtitle="مدل‌های نسخه‌دار تسهیم مراکز هزینه و سرفصل‌ها"
          />
          {rules.status === 'loading' && <LoadingState />}
          {rules.status === 'error' && (
            <StatePanel variant="network_error" title="خطا در دریافت قواعد تسهیم" />
          )}
          {rules.status === 'ready' && rules.data.items.length === 0 && (
            <V2EmptyState
              title="قاعده‌ای تعریف نشده است"
              description="قاعده تسهیم جدید ایجاد کنید."
            />
          )}
          {rules.status === 'ready' && rules.data.items.length > 0 && (
            <V2DataGrid
              columns={[
                {
                  key: 'code',
                  header: 'کد قاعده',
                  render: (row: CostRule) => (
                    <span style={{ fontWeight: 600, fontFamily: 'monospace' }}>{row.code}</span>
                  ),
                },
                { key: 'name', header: 'عنوان قاعده', render: (row: CostRule) => row.name },
                {
                  key: 'version',
                  header: 'نگارش',
                  render: (row: CostRule) => <V2Badge tone="neutral">v{row.version}</V2Badge>,
                },
                {
                  key: 'basis',
                  header: 'مبنای تسهیم',
                  render: (row: CostRule) => row.basisDescription,
                },
                {
                  key: 'targetsCount',
                  header: 'تعداد مقاصد',
                  render: (row: CostRule) => `${row.targets.length} مرکز / حساب`,
                },
                {
                  key: 'status',
                  header: 'وضعیت',
                  render: (row: CostRule) => (
                    <V2Badge tone={row.status === 'active' ? 'success' : 'neutral'}>
                      {row.status === 'active' ? 'فعال' : 'بایگانی'}
                    </V2Badge>
                  ),
                },
                {
                  key: 'actions',
                  header: 'عملیات',
                  render: (row: CostRule) => (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <V2Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setSimRuleId(row.ruleId);
                          setActiveTab('simulate');
                        }}
                      >
                        شبیه‌سازی
                      </V2Button>
                      <V2Button
                        variant="primary"
                        size="sm"
                        onClick={() => {
                          setSelectedRuleId(row.ruleId);
                          setShowExecuteModal(true);
                        }}
                      >
                        اجرای تسهیم
                      </V2Button>
                    </div>
                  ),
                },
              ]}
              rows={rules.data.items}
              rowKey={(row: CostRule) => row.ruleId}
            />
          )}
        </V2Card>
      )}

      {/* Tab 2: Simulation */}
      {activeTab === 'simulate' && (
        <div>
          <V2Card>
            <V2CardHeader
              title="شبیه‌سازی اجرای تسهیم (Dry-Run)"
              subtitle="محاسبه دقیق ریاضی تسهیم بدون هیچ‌گونه تغییر در اسناد یا پایگاه‌داده"
            />
            <form onSubmit={handleRunSimulation} style={{ padding: '0 16px 16px 16px' }}>
              <V2FormGrid>
                <V2FormField label="قاعده تسهیم" required width="medium">
                  <select
                    className="sk-select"
                    value={simRuleId}
                    onChange={(e) => setSimRuleId(e.target.value)}
                    required
                  >
                    <option value="">انتخاب قاعده</option>
                    {rules.status === 'ready' &&
                      rules.data.items.map((r) => (
                        <option key={r.ruleId} value={r.ruleId}>
                          {r.code} — {r.name} (v{r.version})
                        </option>
                      ))}
                  </select>
                </V2FormField>
                <V2FormField label="سال مالی" required width="medium">
                  <select
                    className="sk-select"
                    value={simFiscalYearId}
                    onChange={(e) => setSimFiscalYearId(e.target.value)}
                    required
                  >
                    <option value="">انتخاب سال مالی</option>
                    {years.status === 'ready' &&
                      years.data.items.map((y) => (
                        <option key={y.fiscalYearId} value={y.fiscalYearId}>
                          {y.name}
                        </option>
                      ))}
                  </select>
                </V2FormField>
                <V2FormField label="دوره مالی" required width="medium">
                  <select
                    className="sk-select"
                    value={simFiscalPeriodId}
                    onChange={(e) => setSimFiscalPeriodId(e.target.value)}
                    required
                  >
                    <option value="">انتخاب دوره مالی</option>
                    {periods.status === 'ready' &&
                      periods.data.items
                        .filter((p) => !simFiscalYearId || p.fiscalYearId === simFiscalYearId)
                        .map((p) => (
                          <option key={p.fiscalPeriodId} value={p.fiscalPeriodId}>
                            {p.name}
                          </option>
                        ))}
                  </select>
                </V2FormField>
                <V2FormField label="تا تاریخ (مبنای مانده)" required width="medium">
                  <JalaliDateInput value={simAsOfDate} onChange={setSimAsOfDate} />
                </V2FormField>
              </V2FormGrid>

              <div style={{ marginTop: 16 }}>
                <V2Button type="submit" variant="primary" disabled={busy}>
                  محاسبه شبیه‌سازی
                </V2Button>
              </div>
            </form>
          </V2Card>

          {simulationResult && (
            <div style={{ marginTop: 20 }}>
              <V2Card>
                <V2CardHeader
                  title={`نتیجه شبیه‌سازی قاعده ${simulationResult.ruleCode}`}
                  subtitle={`مجموع مانده قابل تسهیم: ${formatRialFa(simulationResult.poolAmountRial, unit)} ${label} | مغایرت مانده: ${formatRialFa(simulationResult.residualRial, unit)} (تراز قطعی)`}
                />
                <V2DataGrid
                  columns={[
                    {
                      key: 'account',
                      header: 'شناسه سرفصل مقصد',
                      render: (row) => (
                        <span style={{ fontFamily: 'monospace' }}>{row.targetAccountId}</span>
                      ),
                    },
                    {
                      key: 'weight',
                      header: 'ضریب سهم',
                      render: (row) => `${row.weightRatio} سهم`,
                    },
                    {
                      key: 'amount',
                      header: `مبلغ تسهیم‌شده (${label})`,
                      render: (row) => (
                        <span style={{ fontWeight: 600, fontFamily: 'monospace' }}>
                          {formatRialFa(row.allocatedRial, unit)}
                        </span>
                      ),
                    },
                  ]}
                  rows={simulationResult.lines}
                  rowKey={(row) => `${row.targetAccountId}:${row.weightRatio}`}
                />
              </V2Card>
            </div>
          )}
        </div>
      )}

      {/* Tab 3: Runs */}
      {activeTab === 'runs' && (
        <V2Card>
          <V2CardHeader
            title="سوابق اجرای تسهیم دوره‌ای"
            subtitle="اجراهای انجام‌شده و وضعیت اسناد پیش‌نویس تسهیم تولیدی"
          />
          {runs.status === 'loading' && <LoadingState />}
          {runs.status === 'error' && (
            <StatePanel variant="network_error" title="خطا در دریافت سوابق تسهیم" />
          )}
          {runs.status === 'ready' && runs.data.items.length === 0 && (
            <V2EmptyState
              title="اجرایی ثبت نشده است"
              description="از تب قواعد تسهیم، اجرای جدید را کلیک کنید."
            />
          )}
          {runs.status === 'ready' && runs.data.items.length > 0 && (
            <V2DataGrid
              columns={[
                {
                  key: 'date',
                  header: 'تاریخ اجرا',
                  render: (row: CostRun) => displayJalaliDate(row.runDate),
                },
                {
                  key: 'pool',
                  header: `مبلغ مانده تسهیم (${label})`,
                  render: (row: CostRun) => formatRialFa(row.poolAmountRial, unit),
                },
                {
                  key: 'allocated',
                  header: `مبلغ کل تسهیم‌شده (${label})`,
                  render: (row: CostRun) => (
                    <span style={{ fontWeight: 600 }}>
                      {formatRialFa(row.allocatedAmountRial, unit)}
                    </span>
                  ),
                },
                {
                  key: 'residual',
                  header: 'مغایرت مانده',
                  render: (row: CostRun) => (
                    <V2Badge tone={BigInt(row.residualAmountRial) === 0n ? 'success' : 'warn'}>
                      {formatRialFa(row.residualAmountRial, unit)}
                    </V2Badge>
                  ),
                },
                {
                  key: 'journal',
                  header: 'سند حسابداری',
                  render: (row: CostRun) =>
                    row.journalId ? (
                      <Link
                        to={`${base}/journal/${row.journalId}`}
                        style={{
                          color: 'var(--sk-color-primary, #0284c7)',
                          textDecoration: 'underline',
                        }}
                      >
                        مشاهده سند پیش‌نویس
                      </Link>
                    ) : (
                      '—'
                    ),
                },
                {
                  key: 'status',
                  header: 'وضعیت',
                  render: (row: CostRun) => (
                    <V2Badge tone={row.status === 'draft' ? 'warn' : 'success'}>
                      {row.status === 'draft' ? 'پیش‌نویس (منتظر تایید)' : row.status}
                    </V2Badge>
                  ),
                },
              ]}
              rows={runs.data.items}
              rowKey={(row: CostRun) => row.runId}
            />
          )}
        </V2Card>
      )}

      {/* Modal: Create Rule */}
      {showCreateRuleModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
        >
          <div
            style={{
              backgroundColor: 'var(--sk-bg-card, #fff)',
              borderRadius: '12px',
              padding: '24px',
              width: '600px',
              maxWidth: '90vw',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', margin: '-10px -10px -6px' }}
            >
              <V2CloseButton onClick={() => setShowCreateRuleModal(false)} />
            </div>
            <h2 style={{ fontSize: '1.2rem', fontWeight: 700, margin: '0 0 16px 0' }}>
              تعریف قاعده تسهیم بهای تمام‌شده جدید
            </h2>
            <form onSubmit={handleCreateRule}>
              <V2FormGrid>
                <V2FormField label="کد قاعده (مانند ALLOC-OVERHEAD)" required width="full">
                  <input
                    className="sk-input ltr"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    required
                  />
                </V2FormField>
                <V2FormField label="عنوان قاعده" required width="full">
                  <input
                    className="sk-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    placeholder="مثال: تسهیم سربار اجاره دفتر مرکزی"
                  />
                </V2FormField>
                <V2FormField label="حساب مبدا (حساب هزینه قابل تسهیم)" required width="full">
                  <select
                    className="sk-select"
                    value={sourceAccountId}
                    onChange={(e) => setSourceAccountId(e.target.value)}
                    required
                  >
                    <option value="">انتخاب حساب هزینه مبدا</option>
                    {accounts.status === 'ready' &&
                      accounts.data.items.map((a) => (
                        <option key={a.accountId} value={a.accountId}>
                          {a.code} — {a.name}
                        </option>
                      ))}
                  </select>
                </V2FormField>
                <V2FormField label="حساب بستانکار واسط (Absorption Account)" required width="full">
                  <select
                    className="sk-select"
                    value={creditAccountId}
                    onChange={(e) => setCreditAccountId(e.target.value)}
                    required
                  >
                    <option value="">انتخاب حساب واسط بستانکار</option>
                    {accounts.status === 'ready' &&
                      accounts.data.items.map((a) => (
                        <option key={a.accountId} value={a.accountId}>
                          {a.code} — {a.name}
                        </option>
                      ))}
                  </select>
                </V2FormField>
                <V2FormField label="شرح مبنای تسهیم" required width="full">
                  <input
                    className="sk-input"
                    value={basisDescription}
                    onChange={(e) => setBasisDescription(e.target.value)}
                    required
                  />
                </V2FormField>
              </V2FormGrid>

              {/* Targets Rows */}
              <div style={{ marginTop: 20 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 8,
                  }}
                >
                  <h3 style={{ fontSize: '0.95rem', fontWeight: 600, margin: 0 }}>
                    مراکز و حساب‌های مقصد
                  </h3>
                  <V2Button type="button" variant="secondary" size="sm" onClick={addTargetRow}>
                    + افزودن مقصد
                  </V2Button>
                </div>
                {targetRows.map((row, idx) => (
                  <div
                    key={idx}
                    style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}
                  >
                    <div style={{ flex: 3 }}>
                      <select
                        className="sk-select"
                        value={row.targetAccountId}
                        onChange={(e) => {
                          const updated = [...targetRows];
                          updated[idx]!.targetAccountId = e.target.value;
                          setTargetRows(updated);
                        }}
                        required
                      >
                        <option value="">انتخاب حساب مقصد</option>
                        {accounts.status === 'ready' &&
                          accounts.data.items.map((a) => (
                            <option key={a.accountId} value={a.accountId}>
                              {a.code} — {a.name}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div style={{ flex: 1 }}>
                      <input
                        type="number"
                        className="sk-input ltr sk-num"
                        placeholder="ضریب"
                        value={row.weightRatio}
                        onChange={(e) => {
                          const updated = [...targetRows];
                          updated[idx]!.weightRatio = e.target.value;
                          setTargetRows(updated);
                        }}
                        required
                        min={1}
                      />
                    </div>
                    {targetRows.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeTargetRow(idx)}
                        style={{
                          border: 'none',
                          background: 'none',
                          color: 'var(--sk-danger, #ef4444)',
                          cursor: 'pointer',
                          fontSize: '1.2rem',
                        }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 24 }}>
                <V2Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowCreateRuleModal(false)}
                >
                  انصراف
                </V2Button>
                <V2Button type="submit" variant="primary" disabled={busy}>
                  ثبت قاعده تسهیم
                </V2Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Execute Allocation */}
      {showExecuteModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
        >
          <div
            style={{
              backgroundColor: 'var(--sk-bg-card, #fff)',
              borderRadius: '12px',
              padding: '24px',
              width: '450px',
              maxWidth: '90vw',
            }}
          >
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', margin: '-10px -10px -6px' }}
            >
              <V2CloseButton onClick={() => setShowExecuteModal(false)} />
            </div>
            <h2 style={{ fontSize: '1.2rem', fontWeight: 700, margin: '0 0 16px 0' }}>
              اجرای تسهیم بهای تمام‌شده
            </h2>
            <form onSubmit={handleExecuteAllocation}>
              <V2FormGrid>
                <V2FormField label="سال مالی" required width="full">
                  <select
                    className="sk-select"
                    value={execFiscalYearId}
                    onChange={(e) => setExecFiscalYearId(e.target.value)}
                    required
                  >
                    <option value="">انتخاب سال مالی</option>
                    {years.status === 'ready' &&
                      years.data.items.map((y) => (
                        <option key={y.fiscalYearId} value={y.fiscalYearId}>
                          {y.name}
                        </option>
                      ))}
                  </select>
                </V2FormField>
                <V2FormField label="دوره مالی" required width="full">
                  <select
                    className="sk-select"
                    value={execFiscalPeriodId}
                    onChange={(e) => setExecFiscalPeriodId(e.target.value)}
                    required
                  >
                    <option value="">انتخاب دوره مالی</option>
                    {periods.status === 'ready' &&
                      periods.data.items
                        .filter((p) => !execFiscalYearId || p.fiscalYearId === execFiscalYearId)
                        .map((p) => (
                          <option key={p.fiscalPeriodId} value={p.fiscalPeriodId}>
                            {p.name}
                          </option>
                        ))}
                  </select>
                </V2FormField>
                <V2FormField label="تاریخ صدور سند پیش‌نویس" required width="full">
                  <JalaliDateInput value={execRunDate} onChange={setExecRunDate} />
                </V2FormField>
              </V2FormGrid>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
                <V2Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowExecuteModal(false)}
                >
                  انصراف
                </V2Button>
                <V2Button type="submit" variant="primary" disabled={busy}>
                  محاسبه و ایجاد پیش‌نویس
                </V2Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
